import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, computed, Inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyFilterChipsModule, GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyDataSource, GalaxyTableModule, Row } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Association } from '../../shared-services/crm-services/crm-object-service/crm-association.service';
import { LIST_OBJECTS_DEFAULT_PAGE_SIZE, LIST_OBJECTS_PAGE_SIZE_OPTIONS, SNACKBAR_DURATION } from '../../constants';
import { CrmDependencies, CrmInjectionToken, ObjectType, SelectAllAction } from '../../tokens-and-interfaces';
import { TranslationModule } from '../../i18n/translation-module';

import { ListObjectsTableService } from '../list-objects/list-objects-table.service';
import { CrmFiltersService } from '../../shared-services/crm-services/crm-filters.service';
import { CrmObjectService, CRMTrackingService } from '../../shared-services';

import { GalaxyTableSelectionCustomization, GalaxyTableSelectionCustomizationToken } from '@vendasta/galaxy/table';
import { TranslateForCrmObjectPipe, TranslateForCrmObjectService } from '../../i18n';

export interface ListObjectTypeDialogData {
  baseColumnIds: string[];
  modalTitle: string;
  objectType: ObjectType;
  associations: Association[];
  associationType: string;
  newSelection: string;
  enableMultiplePreSelectedRows?: boolean;
  newSelectionIDs?: string[];
  hideCreateButton?: boolean;
  singleSelection?: boolean;
  showActions?: boolean;
  hideActionButton?: boolean;
  filters?: GalaxyFilterInterface[];
  lockedFilters?: GalaxyFilterInterface[];
  unlockPreSelectedRows?: boolean;
  preselectedIds?: string[];
  createButtonText?: string;
}
export interface ListObjectTypeDialogResult {
  selectedRows: Row[];
  clickedCreate?: boolean;
  searchTerm?: string;
  filters?: GalaxyFilterInterface[];
  useSelectAll?: boolean;
  totalObjects?: number;
}
const CRM_PROVIDERS = [CrmObjectService];

@Component({
  selector: 'crm-select-object-type-modal',
  templateUrl: './select-object-type-modal.component.html',
  styleUrls: ['./select-object-type-modal.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    TranslationModule,
    MatDialogModule,
    AsyncPipe,
    GalaxyLoadingSpinnerModule,
    GalaxyEmptyStateModule,
    GalaxyTableModule,
    MatTableModule,
    GalaxyFilterChipsModule,
    TranslateForCrmObjectPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ListObjectsTableService,
    RxState,
    ...CRM_PROVIDERS,
    {
      provide: GalaxyTableSelectionCustomizationToken,
      useValue: {
        rowsSelectionLimit: 100,
      } as GalaxyTableSelectionCustomization,
    },
  ],
})
export class SelectObjectTypeModalComponent {
  protected readonly dataSource: GalaxyDataSource<Row, GalaxyFilterInterface, MatSort> = this.service.dataSource;
  protected readonly columns$: Observable<GalaxyColumnDef[]> = this.service.columns$;
  protected readonly initialLoadingCompleted$: Observable<boolean> = this.service.initialLoadingCompleted$;
  protected readonly showEmptyState$: Observable<boolean> = this.service.showEmptyState$;
  protected readonly objectType = this.data.objectType;
  protected readonly showFiltersApplied$: Observable<boolean> = this.dataSource.filtersApplied$;
  protected readonly isMobile$ = this.breakpointObserver.observe(Breakpoints.XSmall).pipe(
    startWith({
      matches: false,
      breakpoints: {
        [Breakpoints.XSmall]: false,
      },
    } as BreakpointState),
    map((breakpoint: BreakpointState) => breakpoint.matches),
  );
  protected readonly showCreateButton = this.data?.hideCreateButton === undefined ? false : !this.data.hideCreateButton;
  protected readonly createButtonText = this.data?.createButtonText;

  private readonly selectedRows = signal<Row[]>([]);
  protected readonly numSelected = computed(() => this.selectedRows().length);
  protected newSelection: Row[];
  protected readonly preSelectedLockedRows: Row[] =
    this.data.associations.map((association) => {
      return {
        id: association?.associatedObject?.crmObjectId,
        data: {},
      } as Row;
    }) || [];

  protected readonly pageSizeOptions: number[] = LIST_OBJECTS_PAGE_SIZE_OPTIONS;
  protected readonly pageSize: number = LIST_OBJECTS_DEFAULT_PAGE_SIZE;
  protected readonly selectAllActions$: Observable<SelectAllAction[]> = this.createSelectAllActions(this.objectType);
  protected readonly hasSelectAllActions$ = this.selectAllActions$.pipe(map((actions) => actions.length > 0));
  protected readonly hasAssociations = this.data.associations.length > 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ListObjectTypeDialogData,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly service: ListObjectsTableService,
    private dialogRef: MatDialogRef<SelectObjectTypeModalComponent>,
    private readonly snackService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly crmFiltersService: CrmFiltersService,
    private readonly trackingService: CRMTrackingService,
    private readonly translateForCrmObjectService: TranslateForCrmObjectService,
  ) {
    this.crmFiltersService.setInitialFilters([...(this.data.filters ?? []), ...(this.data.lockedFilters ?? [])]);
    this.service.setObjectType(this.data.objectType);
    this.service.setShowActions(false);
    this.service.setBaseColumns(this.data.baseColumnIds);

    const newSelectedRow = [
      {
        id: this.data?.newSelection,
        data: {},
      } as Row,
    ];
    const enablePreselectedRows = this.data?.enableMultiplePreSelectedRows ?? false;
    this.newSelection = enablePreselectedRows ? [] : newSelectedRow;

    this.data.preselectedIds?.forEach((id) => {
      this.newSelection.unshift({
        id,
        data: {},
      } as Row);
    });

    if (!this.hasAssociations) {
      if (this.data.unlockPreSelectedRows) {
        this.newSelection =
          this.data?.newSelectionIDs?.map((id) => {
            return {
              id,
              data: {},
            } as Row;
          }) || [];
      } else {
        this.preSelectedLockedRows =
          this.data?.newSelectionIDs?.map((id) => {
            return {
              id,
              data: {},
            } as Row;
          }) || [];
      }
    }
  }

  updateFilters(filters: GalaxyFilterInterface[]): void {
    this.dataSource.setFilters(filters);
    this.trackingService.trackFilterUsage(this.objectType, filters);
  }

  selectionChanged(rows: Row[]): void {
    if (this.preSelectedLockedRows.length > 0) {
      const filteredData = rows.filter((data) => !this.preSelectedLockedRows.some((item) => item.id === data.id));
      this.selectedRows.set(filteredData);
      return;
    }
    this.selectedRows.set(rows);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  createObjectModal(): void {
    this.dialogRef.close({ selectedRows: this.selectedRows(), clickedCreate: true } as ListObjectTypeDialogResult);
  }

  handleSelectAll() {
    this.dataSource.toggleSelectAll();
  }

  private createSelectAllActions(objectType: ObjectType): Observable<SelectAllAction[]> {
    return objectType === 'Contact' ? (this.config.contact?.selectAllTableActions$ ?? of([])) : of([]);
  }

  async save(): Promise<void> {
    if (this.selectedRows().length > 0) {
      this.dialogRef.close({
        selectedRows: this.selectedRows(),
        useSelectAll: this.dataSource.state.selectAll,
        filters: (this.dataSource.state.filters ?? []).filter((f) => !f.definition?.lockedValue),
        totalObjects: this.dataSource.state.totalDataMembers,
        searchTerm: this.dataSource.state.searchTerm,
      });
    } else {
      const translatedString = await firstValueFrom(
        this.translateForCrmObjectService.getTranslationForCrmObject(this.objectType, 'SELECT_MODAL.NO_SELECTED'),
      );
      this.snackService.openWithOptions(this.translateService.instant(translatedString), {
        duration: SNACKBAR_DURATION,
      });
    }
  }
}
